import React, { useMemo } from 'react';
import { X, Trash2, Eye, Pencil, Archive, Disc, Zap } from 'lucide-react';
import { Button } from '../../../components/ui/button';

export interface PipeFilterColumnOptions {
  onEdit?: (row: any) => void;
  onEditPipeId?: (row: any) => void;
  onEditArchive?: (row: any) => void;
  onEditFlange?: (row: any) => void;
  onEditAnode?: (row: any) => void;
  onDelete?: (row: any) => void;
  onOpenPipeFitter?: (row: any, endType?: 'EndA' | 'EndB') => void;
  canEdit?: boolean;
  /** When false, hide the Archive action button (default exclude-archived view). */
  showArchiveAction?: boolean;
  /** Group ID column only when Pipe Size = All. */
  showGroupIdColumn?: boolean;
}

function flagOn(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

export const usePipeFilterColumns = (options: PipeFilterColumnOptions = {}) => {
  const {
    onEditPipeId,
    onEditArchive,
    onEditFlange,
    onEditAnode,
    onDelete,
    onOpenPipeFitter,
    canEdit = true,
    showArchiveAction = true,
    showGroupIdColumn = false,
  } = options;
  return useMemo(
    () => [
      {
        title: 'SL No',
        dataIndex: 'slNo',
        colWidth: '6%',
        headerClassName: 'text-left',
        cellClassName: 'tabular-nums text-left',
      },
      {
        title: 'Pipe ID',
        dataIndex: 'pipeID',
        colWidth: showGroupIdColumn ? '24%' : '32%',
        headerClassName: 'text-left',
        cellClassName: 'text-left min-w-0',
        render: (value: string, row: any) => (
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate text-[13px] text-gray-900">{value}</span>
            {canEdit ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7 shrink-0 text-blueprint-blue hover:text-blueprint-blue hover:bg-page-bg"
                title="Edit pipe ID"
                onClick={() => onEditPipeId?.(row)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        ),
      },
      ...(showGroupIdColumn
        ? [
            {
              title: 'Group ID',
              dataIndex: 'groupId',
              colWidth: '16%',
              headerClassName: 'text-left',
              cellClassName: 'text-left min-w-0',
              render: (value: string | undefined) => {
                const label =
                  value != null && String(value).trim() !== ''
                    ? String(value).trim()
                    : 'Ungrouped';
                return (
                  <span className="truncate text-[13px] text-gray-900" title={label}>
                    {label}
                  </span>
                );
              },
            },
          ]
        : []),
      {
        title: 'End A',
        dataIndex: 'endA',
        colWidth: showGroupIdColumn ? '10%' : '12%',
        headerClassName: 'text-center px-2',
        align: 'center' as const,
        cellClassName: 'text-center px-2 align-middle',
        render: (value: boolean, row: any) =>
          value ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="mx-auto h-8 w-8 text-blue-500 hover:text-blue-700 hover:bg-blue-100"
              title="Open Pipe Fitter (End A)"
              onClick={() => onOpenPipeFitter?.(row, 'EndA')}
            >
              <Eye className="h-4 w-4" />
            </Button>
          ) : (
            <span
              className="mx-auto inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600"
              title="End A not available"
              aria-label="End A not available"
            >
              <X className="h-4 w-4" />
            </span>
          ),
      },
      {
        title: 'End B',
        dataIndex: 'endB',
        colWidth: showGroupIdColumn ? '10%' : '12%',
        headerClassName: 'text-center px-2',
        align: 'center' as const,
        cellClassName: 'text-center px-2 align-middle',
        render: (value: boolean, row: any) =>
          value ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="mx-auto h-8 w-8 text-blue-500 hover:text-blue-700 hover:bg-blue-100"
              title="Open Pipe Fitter (End B)"
              onClick={() => onOpenPipeFitter?.(row, 'EndB')}
            >
              <Eye className="h-4 w-4" />
            </Button>
          ) : (
            <span
              className="mx-auto inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600"
              title="End B not available"
              aria-label="End B not available"
            >
              <X className="h-4 w-4" />
            </span>
          ),
      },
      {
        title: 'Actions',
        dataIndex: 'id',
        colWidth: showGroupIdColumn ? '26%' : '28%',
        headerClassName: 'text-center px-2',
        align: 'center' as const,
        cellClassName: 'text-center px-2 align-middle',
        render: (_: unknown, row: any) => {
          const archived = flagOn(row.isArchive);
          const flanged = flagOn(row.isFlange);
          const anode = flagOn(row.isAnode);
          return (
            <div className="inline-flex items-center justify-center gap-1.5">
              {canEdit ? (
                <>
                  { (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={
                        archived
                          ? 'h-8 w-8 text-hero-red border-[rgba(235,28,55,0.35)] bg-[rgba(235,28,55,0.06)] hover:bg-[rgba(235,28,55,0.12)]'
                          : 'h-8 w-8 text-blueprint-blue hover:text-blueprint-blue hover:bg-page-bg'
                      }
                      title={archived ? 'Unmark as archive' : 'Mark as archive'}
                      onClick={() => onEditArchive?.(row)}
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className={
                      flanged
                        ? 'h-8 w-8 text-hero-red border-[rgba(235,28,55,0.35)] bg-[rgba(235,28,55,0.06)] hover:bg-[rgba(235,28,55,0.12)]'
                        : 'h-8 w-8 text-blueprint-blue hover:text-blueprint-blue hover:bg-page-bg'
                    }
                    title={flanged ? 'Unmark as flange' : 'Mark as flange'}
                    onClick={() => onEditFlange?.(row)}
                  >
                    <Disc className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className={
                      anode
                        ? 'h-8 w-8 text-hero-red border-[rgba(235,28,55,0.35)] bg-[rgba(235,28,55,0.06)] hover:bg-[rgba(235,28,55,0.12)]'
                        : 'h-8 w-8 text-blueprint-blue hover:text-blueprint-blue hover:bg-page-bg'
                    }
                    title={anode ? 'Unmark as anode' : 'Mark as anode'}
                    onClick={() => onEditAnode?.(row)}
                  >
                    <Zap className="h-4 w-4" />
                  </Button>
                </>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                title="Delete pipe"
                disabled={!canEdit}
                onClick={() => onDelete?.(row)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    [
      onEditPipeId,
      onEditArchive,
      onEditFlange,
      onEditAnode,
      onDelete,
      onOpenPipeFitter,
      canEdit,
      showArchiveAction,
      showGroupIdColumn,
    ]
  );
};
